import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

export type Evidence = Database['public']['Tables']['evidence']['Row'];

interface EvidenceFilters {
  relationType?: string;
  relationId?: string;
}

interface CreateEvidenceData {
  name: string;
  type?: string;
  storage_path?: string;
  notes?: string;
  related_to_type?: string;
  related_to_id?: string;
  org_id: string;
}

export const useEvidence = (orgId?: string, filters?: EvidenceFilters) => {
  const queryClient = useQueryClient();

  const evidenceQuery = useQuery({
    queryKey: ['evidence', orgId, filters?.relationType, filters?.relationId],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('evidence')
        .select('*')
        .eq('org_id', orgId);

      if (filters?.relationType && filters?.relationId) {
        query = query
          .eq('related_to_type', filters.relationType)
          .eq('related_to_id', filters.relationId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return data ?? [];
    },
    enabled: !!orgId,
  });

  const createEvidenceMutation = useMutation({
    mutationFn: async (data: CreateEvidenceData) => {
      const evidenceName = data.name?.trim();
      if (!evidenceName) {
        throw new Error('Evidence name is required');
      }

      const { error } = await supabase.from('evidence').insert([{
        org_id: data.org_id,
        name: evidenceName,
        type: data.type ?? 'document',
        storage_path: data.storage_path ?? null,
        notes: data.notes ?? null,
        related_to_type: data.related_to_type ?? null,
        related_to_id: data.related_to_id ?? null,
      }]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence'] });
      toast.success('Evidence added successfully');
    },
    onError: (error) => {
      console.error('Error adding evidence:', error);
      toast.error('Failed to add evidence');
    },
  });

  const deleteEvidenceMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete related links
      await supabase.from('links').delete().or(`source_id.eq.${id},target_id.eq.${id}`);
      
      // Then delete the evidence
      const { error } = await supabase.from('evidence').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evidence'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      toast.success('Evidence deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting evidence:', error);
      toast.error('Failed to delete evidence');
    },
  });

  return {
    evidence: evidenceQuery.data ?? [],
    isLoading: evidenceQuery.isLoading,
    error: evidenceQuery.error,
    createEvidence: createEvidenceMutation.mutate,
    deleteEvidence: deleteEvidenceMutation.mutate,
    isCreating: createEvidenceMutation.isPending,
    isDeleting: deleteEvidenceMutation.isPending,
  };
};
